import { WebStorageStateStore } from 'oidc-client-ts';

export interface CognitoAuthConfigOptions {
  /** The user pool's issuer, e.g. `https://cognito-idp.<region>.amazonaws.com/<pool id>`. */
  authority: string;
  clientId: string;
  clientSecret?: string;
}

/**
 * The secret, or `undefined` for an app client without one. The ECS image
 * fills in a `___VITE_…___` placeholder after the build; when the environment
 * has no secret the placeholder (or an empty value) is left behind, and sending
 * it makes Cognito refuse every sign-in ("not configured for secret but secret
 * hash was received"). Cognito's secrets are letters and digits only, so a
 * pattern tells the two apart — and, unlike a literal comparison, it is not
 * folded away by the minifier before the placeholder is replaced.
 */
const appClientSecret = (secret?: string) =>
  secret && /^[a-z0-9]+$/i.test(secret) ? secret : undefined;

/**
 * `<AuthProvider>` settings for a Cognito app client, shared by both apps so
 * the sign-in page and the provider agree on them.
 *
 * The app's origin is both the sign-in callback and the sign-out return URL.
 *
 * The session lives in localStorage, not oidc-client-ts's default
 * sessionStorage: sessionStorage is per tab, so every new tab used to start
 * signed out. `Authenticate` keeps already-open tabs in step with it.
 */
export const createCognitoAuthConfig = ({
  authority,
  clientId,
  clientSecret,
}: CognitoAuthConfigOptions) => {
  const origin = window.location.origin;

  return {
    authority,
    client_id: clientId,
    client_secret: appClientSecret(clientSecret),
    redirect_uri: origin,
    post_logout_redirect_uri: origin,
    response_type: 'code',
    scope: 'email openid phone profile',
    automaticSilentRenew: true,
    // The id_token already carries every claim the apps read (sub, email,
    // given_name, cognito:groups). Cognito's userInfo endpoint also rejects
    // the API-issued access tokens the own sign-in page stores, so loading
    // it would end those sessions at their first silent renew.
    loadUserInfo: false,
    clearStaleState: true,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
  };
};
